import React from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Badge from '@shared-components/atoms/Badge';
import { ProductUpchargeBadge } from '@shared-components/molecules/ProductTile';
import clsx from 'clsx';
import InfoIcon from '../../../../localization/icons/InfoIcon';
import { DisplayableModifier } from '@/domain/product/types';
import useStyles from '../styles';
import { SodiumWarningIcon } from '@/clientCore/components/SodiumWarningDisclaimer/components/SodiumWarningIcon';
import useSodiumAlertForStore from '@/clientCore/components/SodiumWarningDisclaimer/hooks/useSodiumAlertForStore';
import formattedPrice from '@/common/formattedPrice';

interface ProductInfoProps {
  name: string;
  formattedUnitPrice: string;
  showPrice?: boolean;
  lowerCaseCategory?: string;
  description?: string;
  sizes?: DisplayableModifier[];
  handleInfoButtonClick?: () => void;
  categoryName?: string;
  isProductOutOfStock?: boolean;
  formattedSelectedOption?: string;
  sodiumWarning?: boolean;
  upcharge?: number;
  badges?: {
    new?: boolean;
    upcharge?: boolean;
    soldOut?: boolean;
  };
}

const ProductInfo = (props: ProductInfoProps): JSX.Element => {
  const classes = useStyles({});
  const {
    name,
    sizes,
    description,
    lowerCaseCategory,
    handleInfoButtonClick,
    formattedUnitPrice,
    categoryName,
    showPrice = true,
    formattedSelectedOption,
    isProductOutOfStock = false,
    sodiumWarning = false,
    upcharge,
    badges
  } = props;
  const { isShowSodiumAlertForStore } = useSodiumAlertForStore();
  const showSodiumWarning = isShowSodiumAlertForStore && sodiumWarning;
  // Define if the size name will show up with the product name, only for drinks category
  const isDrinksAndHasOnlyOneSize = lowerCaseCategory === 'drinks' && sizes?.length === 1;

  return (
    <div className={classes.priceAndNameContainer}>
      <Grid className={classes.productInfoContainer}>
        <div>
          {badges?.new && (
            <Badge data-testid="menu-product-new-badge" variant="primary" className={classes.badge}>
              New
            </Badge>
          )}
          <Typography
            className={clsx(classes.title, isProductOutOfStock ? classes.title_oos : undefined)}
            data-testid="localized-product-tile-name"
          >
            {name}
            {isDrinksAndHasOnlyOneSize ? ` - ${sizes[0].name}` : ''}
          </Typography>
        </div>
        {description && categoryName ? (
          <IconButton
            aria-label="Product Info"
            aria-haspopup="dialog"
            disableFocusRipple
            disableRipple
            className={classes.infoIcon}
            onClick={handleInfoButtonClick}
            size="large"
          >
            <InfoIcon />
          </IconButton>
        ) : null}
      </Grid>
      {badges?.soldOut && <Badge variant="disabled">Sold Out</Badge>}
      {badges?.upcharge && (
        <ProductUpchargeBadge upchargeAmount={formattedPrice(upcharge, (upcharge ?? 0) % 100 === 0)} />
      )}
      {formattedSelectedOption && (
        <Typography className={classes.price} data-testid="localized-product-tile-selected-option">
          {formattedSelectedOption}
        </Typography>
      )}
      {showPrice && (
        <Typography className={classes.price} data-testid="localized-product-tile-price">
          {formattedUnitPrice}
        </Typography>
      )}
      {showSodiumWarning && (
        <Typography className={classes.sodiumWarning} data-testid="localized-product-tile-sodium-warning">
          <SodiumWarningIcon width="116" height="14" />
        </Typography>
      )}
    </div>
  );
};

export default ProductInfo;
